import React, {FC, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import DOMPurify from 'dompurify'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'

interface StructuredRemarksViewProps {
  text?: string | null
  emptyMessage?: string
  maxHeight?: string | number
  format?: 'plain' | 'html'
}

export const StructuredRemarksView: FC<StructuredRemarksViewProps> = ({
  text,
  emptyMessage = 'No remarks recorded.',
  maxHeight = '450px',
  format = 'plain',
}) => {
  if (!text || !text.trim()) {
    return (
      <div className='p-4 text-center text-muted bg-light rounded border border-dashed'>
        <span className='fs-7 fst-italic'>{emptyMessage}</span>
      </div>
    )
  }

  return (
    <div
      className='p-4 rounded border bg-white shadow-xs overflow-auto'
      style={{
        maxHeight,
        fontSize: '13.5px',
        lineHeight: '1.7',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        minWidth: 0,
        color: '#2b2f32',
      }}
    >
      {format === 'html' ? (
        <div className='ql-snow ql-editor p-0' dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(text)}} />
      ) : text}
    </div>
  )
}

interface SCMRemarksModalProps {
  visible: boolean
  title: string
  subtitle?: string
  remarksText?: string | null
  onClose: () => void
}

export const SCMRemarksModal: FC<SCMRemarksModalProps> = ({
  visible,
  title,
  subtitle,
  remarksText,
  onClose,
}) => {
  const titleId = useId()
  useEffect(() => {
    if (!visible) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {event.stopPropagation(); onClose()}
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [visible, onClose])
  if (!visible) return null

  const handleCopy = () => {
    if (!remarksText) return
    navigator.clipboard.writeText(remarksText)
    toast.success('Remarks copied to clipboard', {autoClose: 2000})
  }

  return createPortal(
    <div
      style={{
        background: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        inset: 0,
        zIndex: 1060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      tabIndex={-1}
      onClick={onClose}
    >
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        style={{
          width: '90vw',
          maxWidth: '750px',
          maxHeight: '85vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <div className='d-flex flex-column'>
            <div className='d-flex align-items-center gap-2'>
              <KTSVG
                path='/media/icons/duotune/general/gen005.svg'
                className='svg-icon-2 svg-icon-primary'
              />
              <h5 id={titleId} className='modal-title text-dark m-0 fw-bold'>{title}</h5>
            </div>
            {subtitle && <span className='text-muted fs-7 mt-1'>{subtitle}</span>}
          </div>
          <div className='d-flex align-items-center gap-2'>
            {remarksText && remarksText.trim() && (
              <button
                type='button'
                className='btn btn-light-primary btn-sm d-flex align-items-center gap-1 py-2 px-3'
                onClick={handleCopy}
                title='Copy full remarks'
              >
                <KTSVG
                  path='/media/icons/duotune/general/gen054.svg'
                  className='svg-icon-4'
                />
                Copy
              </button>
            )}
            <button
              type='button'
              className='btn-close ms-2'
              aria-label='Close'
              onClick={onClose}
            />
          </div>
        </div>

        {/* Modal Body */}
        <div className='flex-grow-1 overflow-auto p-5 bg-light-subtle' style={{minHeight: 0}}>
          <StructuredRemarksView text={remarksText} maxHeight='calc(85vh - 160px)' />
        </div>

        {/* Modal Footer */}
        <div className='border-top d-flex justify-content-between align-items-center px-5 py-3 flex-shrink-0 bg-white'>
          <span className='text-muted fs-8'>
            {remarksText ? `${remarksText.length} characters` : 'Empty'}
          </span>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>, document.body
  )
}

export const RemarksModal = SCMRemarksModal
export type RemarksModalProps = SCMRemarksModalProps

interface RemarksCellProps {
  text?: string | null
  title: string
  subtitle?: string
  onOpenModal?: (title: string, subtitle: string, text: string) => void
}

export const RemarksCell: FC<RemarksCellProps> = ({
  text,
  title,
  subtitle = '',
  onOpenModal,
}) => {
  const [showFullRemarks, setShowFullRemarks] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const previewRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const preview = previewRef.current
    if (!preview) return
    const measure = () => setOverflows(preview.scrollHeight > preview.clientHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(preview)
    return () => observer.disconnect()
  }, [text])
  if (!text || !text.trim()) {
    return <span className='text-muted fs-7'>-</span>
  }

  const isLong = text.length > 60 || text.split(/\r?\n/).length > 2 || overflows

  return (
    <>
    <div
      className='remarks-cell-container'
      style={{
        maxWidth: '220px',
        minWidth: '180px',
      }}
    >
      <div
        ref={previewRef}
        className='text-gray-700 fs-7'
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: '1.4',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          whiteSpace: 'pre-wrap',
        }}
        title={text}
      >
        {text}
      </div>
      {isLong && (
        <button
          type='button'
          className='btn btn-link btn-xs p-0 mt-1 text-primary fw-semibold text-decoration-none d-inline-flex align-items-center'
          style={{fontSize: '11px'}}
          onClick={(e) => {
            e.stopPropagation()
            if (onOpenModal) onOpenModal(title, subtitle, text)
            else setShowFullRemarks(true)
          }}
        >
          <span>View remarks</span>
          <span className='ms-1'>&rarr;</span>
        </button>
      )}
    </div>
    {!onOpenModal && <RemarksModal visible={showFullRemarks} title={title} subtitle={subtitle}
      remarksText={text} onClose={() => setShowFullRemarks(false)} />}
    </>
  )
}
